import * as THREE from 'three';
const TMP = {
    vA: new THREE.Vector3(),
    vB: new THREE.Vector3(),
    scale: new THREE.Vector3(),
};
/**
 * Per-side facing with sane defaults:
 *  - only end   (A→B):           end=towardTarget
 *  - only start (A←B):           start=awayFromTarget (points into A)
 *  - twoSided   (A⇄B):           start=awayFromTarget, end=towardTarget
 *  - else: use explicit per-side, else global, else 'towardTarget'
 */
export function resolveFacing(arrows, side) {
    var _a, _b, _c;
    if (!arrows)
        return 'towardTarget';
    const explicit = side === 'start' ? arrows.startFacing : arrows.endFacing;
    if (explicit)
        return explicit;
    const onlyStart = !!arrows.start && !arrows.end && !arrows.twoSided;
    const onlyEnd = !!arrows.end && !arrows.start && !arrows.twoSided;
    if (arrows.twoSided)
        return side === 'start' ? 'awayFromTarget' : 'towardTarget';
    if (onlyStart)
        return side === 'start' ? 'awayFromTarget' : ((_a = arrows.facing) !== null && _a !== void 0 ? _a : 'towardTarget');
    if (onlyEnd)
        return side === 'end' ? 'towardTarget' : ((_b = arrows.facing) !== null && _b !== void 0 ? _b : 'towardTarget');
    return (_c = arrows.facing) !== null && _c !== void 0 ? _c : 'towardTarget';
}
// small helpers
export function v3FromPositions(positions, i) {
    return new THREE.Vector3(positions[i * 3 + 0], positions[i * 3 + 1], positions[i * 3 + 2]);
}
export function getEffectiveRadius(orb) {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    if (!orb.group)
        return (_b = (_a = orb.orbConfig) === null || _a === void 0 ? void 0 : _a.radius) !== null && _b !== void 0 ? _b : 100;
    const outer = orb.getOutermostLayer();
    if (!outer || !outer.geometry) {
        return (_d = (_c = orb.orbConfig) === null || _c === void 0 ? void 0 : _c.radius) !== null && _d !== void 0 ? _d : 100;
    }
    // Ensure bounding sphere exists
    if (!outer.geometry.boundingSphere) {
        outer.geometry.computeBoundingSphere();
    }
    const base = (_f = (_e = outer.geometry.boundingSphere) === null || _e === void 0 ? void 0 : _e.radius) !== null && _f !== void 0 ? _f : ((_h = (_g = orb.orbConfig) === null || _g === void 0 ? void 0 : _g.radius) !== null && _h !== void 0 ? _h : 100);
    outer.getWorldScale(TMP.scale);
    // assume uniform scale (your shapes use setScalar)
    return base * TMP.scale.x;
}
export function computeSurfacePoints(a, b) {
    if (!a.group || !b.group)
        throw new Error('computeSurfacePoints: orb missing group');
    const aCenter = a.group.getWorldPosition(TMP.vA.set(0, 0, 0)).clone();
    const bCenter = b.group.getWorldPosition(TMP.vB.set(0, 0, 0)).clone();
    const dir = bCenter.clone().sub(aCenter);
    const len = Math.max(dir.length(), 1e-6);
    dir.multiplyScalar(1 / len); // normalize
    const rA = getEffectiveRadius(a);
    const rB = getEffectiveRadius(b);
    const start = aCenter.clone().add(dir.clone().multiplyScalar(rA));
    const end = bCenter.clone().add(dir.clone().multiplyScalar(-rB));
    return { start, end };
}
/**
 * Returns a style based on declared relationships.
 * Prefers an explicit relationship from A->B; falls back to B->A; else default.
 */
export function getLineStyle(a, b) {
    const styleFor = (rels) => {
        if (!rels || !a.orbType || !b.orbType)
            return undefined;
        const rel = rels.find(r => r.targetOrbType === b.orbType);
        if (!rel)
            return undefined;
        switch (rel.type) {
            case 'connect': return { color: 0x2196f3, width: 4 }; // blue + thick
            case 'producerOf': return { color: 0x4caf50, width: 4 };
            case 'consumerOf': return { color: 0x2196f3, width: 4 };
            case 'attract': return { color: 0x9c27b0, width: 4 };
            case 'repel': return { color: 0xf44336, width: 4 };
            case 'group': return { color: 0x90a4ae, width: 4 };
            case 'orbit': return { color: 0xff9800, width: 4 };
            default: return { color: 0x2196f3, width: 4 };
        }
    };
    return styleFor(a.relationships)
        || styleFor(b.relationships)
        || { color: 0x2196f3, width: 4 }; // default: blue + thick
}
export function getEdgeProfileFromRelationships(a, b) {
    var _a, _b, _c, _d, _e;
    if (!a.relationships || !a.orbType || !b.orbType)
        return;
    const rel = a.relationships.find(r => r.targetOrbType === b.orbType);
    if (!rel)
        return;
    if (!rel.edgeProfile)
        return;
    return typeof rel.edgeProfile === 'string'
        ? ((_c = (_b = (_a = a.orbConfig) === null || _a === void 0 ? void 0 : _a.edgeProfiles) === null || _b === void 0 ? void 0 : _b[rel.edgeProfile]) !== null && _c !== void 0 ? _c : (_e = (_d = b.orbConfig) === null || _d === void 0 ? void 0 : _d.edgeProfiles) === null || _e === void 0 ? void 0 : _e[rel.edgeProfile])
        : rel.edgeProfile;
}
export function firstVec3(positions, i) {
    return new THREE.Vector3(positions[i * 3 + 0], positions[i * 3 + 1], positions[i * 3 + 2]);
}
/** Endpoint tangents from the sampled curve (start→end). */
export function getEndpointTangents(positions) {
    const n = Math.max(positions.length / 3, 0);
    if (n < 2) {
        const up = new THREE.Vector3(0, 1, 0);
        return { tStart: up.clone(), tEnd: up.clone() };
    }
    const p0 = v3FromPositions(positions, 0);
    const p1 = v3FromPositions(positions, Math.min(1, n - 1));
    const pn1 = v3FromPositions(positions, Math.max(n - 2, 0));
    const pn = v3FromPositions(positions, n - 1);
    const tStart = p1.clone().sub(p0); // into the curve from the start
    const tEnd = pn.clone().sub(pn1); // toward the end
    if (tStart.lengthSq() < 1e-12)
        tStart.set(0, 1, 0);
    if (tEnd.lengthSq() < 1e-12)
        tEnd.set(0, 1, 0);
    tStart.normalize();
    tEnd.normalize();
    return { tStart, tEnd };
}
/** Align mesh +Y to dir and place it at pos. */
export function orientArrow(mesh, pos, dir) {
    mesh.position.copy(pos);
    const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
    mesh.quaternion.copy(q);
}
/**
 * Compute a safe arrow placement that never overlaps the orb:
 *  - Uses the local curve tangent for direction (not global A→B).
 *  - Offsets by (extraOffset + clearance) in the direction the arrow points.
 */
export function computeArrowPlacement(atPoint, // surface point for start or end (already includes extraOffset*)
tangent, // unit tangent at that endpoint
which, facing, // resolved per-side facing
profile, arrowSize) {
    var _a, _b, _c, _d, _e, _f;
    const extraStart = (_b = (_a = profile.style) === null || _a === void 0 ? void 0 : _a.extraOffsetStart) !== null && _b !== void 0 ? _b : 0;
    const extraEnd = (_d = (_c = profile.style) === null || _c === void 0 ? void 0 : _c.extraOffsetEnd) !== null && _d !== void 0 ? _d : 0;
    // keep the cone off the surface (used only for "pointing away" cases below)
    const userClear = which === 'start' ? (_e = profile.arrows) === null || _e === void 0 ? void 0 : _e.clearanceStart : (_f = profile.arrows) === null || _f === void 0 ? void 0 : _f.clearanceEnd;
    const arrowClear = Math.max(userClear !== null && userClear !== void 0 ? userClear : (arrowSize * 1.1), 4);
    const baseOffset = which === 'start' ? extraStart : extraEnd;
    const totalOffset = baseOffset + arrowClear;
    // Arrow axis: +Y -> dir
    let dir = tangent.clone().normalize();
    const halfHeight = arrowSize; // ConeGeometry(size, height=2*size) ⇒ tip at +size, base at -size from the mesh origin
    if (which === 'start') {
        // Start tangent points A→B. "awayFromTarget" means the arrow points into A (toward the node).
        if (facing === 'awayFromTarget') {
            dir.multiplyScalar(-1); // now dir points toward A (the tip direction)
            // TIP-ANCHOR: put the cone TIP exactly at the line's start point
            // tip = center + dir * halfHeight  ⇒  center = tip - dir * halfHeight
            const pos = atPoint.clone().sub(dir.clone().multiplyScalar(halfHeight));
            return { pos, dir };
        }
        else {
            // pointing away from A (toward B): keep your clearance-based placement
            const pos = atPoint.clone().add(dir.clone().multiplyScalar(totalOffset));
            return { pos, dir };
        }
    }
    else {
        // which === 'end', end tangent points toward B
        if (facing === 'towardTarget') {
            // Arrow points into B. TIP-ANCHOR to the line's end point.
            const pos = atPoint.clone().sub(dir.clone().multiplyScalar(halfHeight));
            return { pos, dir };
        }
        else {
            // pointing away from B (toward A): keep clearance-based placement
            dir.multiplyScalar(-1);
            const pos = atPoint.clone().add(dir.clone().multiplyScalar(totalOffset));
            return { pos, dir };
        }
    }
}
